import type { CreditTransaction, CreditTransactionType } from '@houzy/schemas/partner-cockpit/credits';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useEffect, useId, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ListPagination } from '../../../components/ListPagination';
import { TextLink } from '../../../components/TextLink';
import { readSessionJson, writeSessionJson } from '../../../lib/sessionStore';
import { useCreditTransactions, useCreditsWallet } from '../api/creditsApi';
import { settingsTabPath } from '../routes';

const TRANSACTION_DESCRIPTION_KEY: Record<CreditTransactionType, string> = {
    OfferRequestDelivery: 'SETTINGS.CREDITS.DESCRIPTION.OFFER_REQUEST_DELIVERY',
    OfferRequestReimbursement: 'SETTINGS.CREDITS.DESCRIPTION.OFFER_REQUEST_REIMBURSEMENT',
    PartnerProductPayment: 'SETTINGS.CREDITS.DESCRIPTION.PARTNER_PRODUCT_PAYMENT',
    ProductCancellation: 'SETTINGS.CREDITS.DESCRIPTION.PRODUCT_CANCELLATION',
    Correction: 'SETTINGS.CREDITS.DESCRIPTION.CORRECTION',
    ReservedForSubscriptionMigration: 'SETTINGS.CREDITS.DESCRIPTION.RESERVED_FOR_SUBSCRIPTION_MIGRATION',
    ReleasedFromSubscriptionMigration: 'SETTINGS.CREDITS.DESCRIPTION.RELEASED_FROM_SUBSCRIPTION_MIGRATION',
    MigratedToSubscription: 'SETTINGS.CREDITS.DESCRIPTION.MIGRATED_TO_SUBSCRIPTION',
};

const DEFAULT_ROWS_PER_PAGE = 5;
const PAGINATION_SESSION_KEY = 'credits.transactions.pagination';

function readSessionPagination(): { page: number; rowsPerPage: number } {
    return readSessionJson(PAGINATION_SESSION_KEY, { page: 0, rowsPerPage: DEFAULT_ROWS_PER_PAGE }, (stored) => ({
        page: typeof stored.page === 'number' && stored.page >= 0 ? stored.page : 0,
        rowsPerPage:
            typeof stored.rowsPerPage === 'number' && stored.rowsPerPage > 0
                ? stored.rowsPerPage
                : DEFAULT_ROWS_PER_PAGE,
    }));
}

function signedCredits(transaction: CreditTransaction): string {
    return transaction.credits < 0 ? `${transaction.credits}` : `+${transaction.credits}`;
}

function creditsColor(transaction: CreditTransaction): string {
    return transaction.credits < 0 ? 'error.main' : 'success.main';
}

function TransactionDescription({ transaction }: { transaction: CreditTransaction }): ReactNode {
    const { t } = useTranslation();
    const label = t(TRANSACTION_DESCRIPTION_KEY[transaction.type]);
    if (!('offerRequestReferenceId' in transaction)) {
        return label;
    }

    const description = `${label} · #${transaction.offerRequestReferenceId}`;
    if (transaction.offerRequestLeadUuid === null) {
        return description;
    }
    return (
        <TextLink
            to="/open-requests/$offerRequestLeadUuid"
            params={{ offerRequestLeadUuid: transaction.offerRequestLeadUuid }}
            color="inherit">
            {description}
        </TextLink>
    );
}

export function CreditsSettingsPage(): ReactElement {
    const { t, i18n } = useTranslation();
    const headingId = useId();
    const initialPagination = useMemo(readSessionPagination, []);
    const [page, setPage] = useState(initialPagination.page);
    const [rowsPerPage, setRowsPerPage] = useState(initialPagination.rowsPerPage);

    useEffect(() => {
        writeSessionJson(PAGINATION_SESSION_KEY, { page, rowsPerPage });
    }, [page, rowsPerPage]);

    const wallet = useCreditsWallet();
    const transactions = useCreditTransactions(page, rowsPerPage);

    const transactionDate = (transaction: CreditTransaction): string =>
        new Date(transaction.date).toLocaleDateString(i18n.language);

    const items = transactions.data?.items ?? [];
    const total = transactions.data?.total ?? 0;
    const hasError = wallet.isError || transactions.isError;

    return (
        <Stack spacing={{ xs: 2, md: 3 }}>
            <Card variant="outlined" component="section" aria-labelledby={headingId}>
                <CardHeader
                    title={t('SETTINGS.CREDITS.MANAGE_SUBSCRIPTION')}
                    slotProps={{ title: { variant: 'h6', component: 'h2', id: headingId } }}
                />
                <CardContent sx={{ pt: { xs: 2, md: 0 } }}>
                    <Stack spacing={3}>
                        <Alert severity="warning" variant="outlined">
                            <AlertTitle>{t('SETTINGS.CREDITS.CREDITS_DISABLED_TITLE')}</AlertTitle>
                            <Typography variant="body2">
                                {t('SETTINGS.CREDITS.CREDITS_DISABLED_BODY_PREFIX')}{' '}
                                <TextLink to={settingsTabPath('subscription')} color="inherit">
                                    {t('SETTINGS.TABS.SUBSCRIPTION')}
                                </TextLink>{' '}
                                {t('SETTINGS.CREDITS.CREDITS_DISABLED_BODY_SUFFIX')}
                            </Typography>
                        </Alert>

                        {hasError ? (
                            <Alert severity="error" variant="outlined">
                                {t('SETTINGS.CREDITS.LOAD_ERROR')}
                            </Alert>
                        ) : (
                            <>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                                        {t('SETTINGS.CREDITS.CURRENT_CREDITS_TITLE')}
                                    </Typography>
                                    <Alert severity="success" variant="outlined" icon={false}>
                                        <Typography variant="caption" color="text.secondary" component="p">
                                            {t('SETTINGS.CREDITS.AVAILABLE_CREDITS')}
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="baseline">
                                            <Typography variant="h4" component="p" color="success.main">
                                                {wallet.isPending ? (
                                                    <CircularProgress size={28} aria-label={t('ACTIONS.LOADING')} />
                                                ) : (
                                                    wallet.data?.availableCredits
                                                )}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('SETTINGS.CREDITS.CREDITS_REMAINING')}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('SETTINGS.CREDITS.UNLIMITED_WITH_SUBSCRIPTION')}
                                        </Typography>
                                    </Alert>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                        {t('SETTINGS.CREDITS.RECENT_ACTIVITY')}
                                    </Typography>
                                    <Paper variant="outlined">
                                        {transactions.isPending ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                                <CircularProgress size={24} aria-label={t('ACTIONS.LOADING')} />
                                            </Box>
                                        ) : items.length === 0 ? (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ p: 2, textAlign: 'center' }}>
                                                {t('SETTINGS.CREDITS.EMPTY')}
                                            </Typography>
                                        ) : (
                                            <>
                                                <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>
                                                                    {t('SETTINGS.CREDITS.TABLE.DATE')}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {t('SETTINGS.CREDITS.TABLE.DESCRIPTION')}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {t('SETTINGS.CREDITS.TABLE.CREDITS')}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {t('SETTINGS.CREDITS.TABLE.BALANCE')}
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {items.map((row) => (
                                                                <TableRow key={row.uuid}>
                                                                    <TableCell>{transactionDate(row)}</TableCell>
                                                                    <TableCell>
                                                                        <TransactionDescription transaction={row} />
                                                                    </TableCell>
                                                                    <TableCell
                                                                        align="right"
                                                                        sx={{
                                                                            color: creditsColor(row),
                                                                            fontWeight: 600,
                                                                        }}>
                                                                        {signedCredits(row)}
                                                                    </TableCell>
                                                                    <TableCell align="right">{row.balance}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>

                                                <Stack
                                                    divider={<Divider />}
                                                    sx={{ display: { xs: 'flex', md: 'none' } }}>
                                                    {items.map((row) => (
                                                        <Stack
                                                            key={row.uuid}
                                                            direction="row"
                                                            spacing={2}
                                                            sx={{ p: 2, justifyContent: 'space-between' }}>
                                                            <Stack spacing={0.5}>
                                                                <Typography variant="body2">
                                                                    <TransactionDescription transaction={row} />
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {transactionDate(row)}
                                                                </Typography>
                                                            </Stack>
                                                            <Stack spacing={0.5} sx={{ textAlign: 'right' }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: creditsColor(row),
                                                                        fontWeight: 600,
                                                                    }}>
                                                                    {signedCredits(row)}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {t('SETTINGS.CREDITS.TABLE.BALANCE')}: {row.balance}
                                                                </Typography>
                                                            </Stack>
                                                        </Stack>
                                                    ))}
                                                </Stack>
                                            </>
                                        )}

                                        <Box sx={{ p: 2 }}>
                                            <ListPagination
                                                page={page}
                                                pageSize={rowsPerPage}
                                                total={total}
                                                pageSizeOptions={[5, 10, 25]}
                                                onPageChange={setPage}
                                                onPageSizeChange={(nextPageSize) => {
                                                    setRowsPerPage(nextPageSize);
                                                    setPage(0);
                                                }}
                                                itemsPerPageLabel={t('SETTINGS.CREDITS.ITEMS_PER_PAGE')}
                                                paginationAriaLabel={t('SETTINGS.CREDITS.PAGINATION_ARIA')}
                                            />
                                        </Box>
                                    </Paper>
                                </Box>
                            </>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </Stack>
    );
}
